import type { ReactNode } from 'react';
import Link from 'next/link';
import { PortalLink } from '../portal/PortalLink';
import { sbText } from '../portal/i18nCatalogue';

/**
 * Sub-shell for blog/wiki content modules — title strip + responsive
 * 1-to-3 column body (optional sidebar + main + optional aside).
 *
 * <h3>Responsive layout</h3>
 * <ul>
 *   <li>{@code <768px}: single column. Sidebar moves to the top and
 *       scroll-snaps on touch. Aside is hidden.</li>
 *   <li>{@code 768-1023px}: sidebar appears as a real column, aside
 *       hidden (too narrow to be useful).</li>
 *   <li>{@code ≥1024px}: sidebar + main + aside as a 3-column grid.</li>
 * </ul>
 *
 * <h3>Accessibility</h3>
 *
 * Skip link, semantic landmarks ({@code main}, {@code nav},
 * {@code aside}, {@code article}), {@code :focus-visible} keyboard
 * styling. Search slot is wrapped in {@code <search role="search">}.
 * Mobile tap targets ≥44px per WCAG 2.5.5.
 */
export type ContentChromeMode = 'full' | 'minimal' | 'immersive';

export interface ContentModuleShellProps {
  title: string;
  subtitle?: string;
  /** Uppercase eyebrow above the title. Suppressed in
   *  {@code 'minimal'} mode regardless. Pass {@code null} or omit to
   *  skip. */
  kicker?: string | null;
  /** Optional href that turns the title into a link. */
  titleHref?: string;
  /** Search slot. Caller supplies the input; the shell wraps in
   *  {@code <search role="search">}. */
  search?: ReactNode | null;
  /** Sidebar slot — typically a {@code <nav>} from the caller. The
   *  shell wraps in {@code <nav aria-label={sidebarLabel}>}. Nested
   *  {@code <nav>} elements are legal. */
  sidebar?: ReactNode | null;
  /** Right-rail slot — typically a TOC. Wrapped in {@code <aside>}. */
  aside?: ReactNode | null;
  children: ReactNode;
  mode?: ContentChromeMode;
  tone?: 'auto' | 'reading' | 'cinematic';
  /** Aria-label for the sidebar landmark. Defaults to the catalogue's
   *  {@code contentModule.sidebarLabel}. */
  sidebarLabel?: string;
  /** Aria-label for the aside landmark. Defaults to the catalogue's
   *  {@code contentModule.asideLabel}. */
  asideLabel?: string;
}

export function ContentModuleShell({
  title,
  subtitle,
  kicker,
  titleHref,
  search,
  sidebar,
  aside,
  children,
  mode = 'full',
  tone = 'auto',
  sidebarLabel,
  asideLabel,
}: ContentModuleShellProps) {
  const navLabel = sidebarLabel ?? sbText('contentModule.sidebarLabel');
  const tocLabel = asideLabel ?? sbText('contentModule.asideLabel');
  const hasAside = aside !== undefined && aside !== null;
  const hasSidebar = sidebar !== undefined && sidebar !== null;
  const hasSearch = search !== undefined && search !== null;

  const isMinimal = mode === 'minimal';
  const isImmersive = mode === 'immersive';

  const verticalPad = isMinimal ? 16 : isImmersive ? 56 : 32;
  const stripPadY = isMinimal ? 14 : isImmersive ? 36 : 24;
  const titleSize = isMinimal ? 22 : isImmersive ? 44 : 30;
  const titleWeight = isImmersive ? 700 : 600;
  const showKicker = !isMinimal && typeof kicker === 'string' && kicker.length > 0;
  const showTitle = typeof title === 'string' && title.trim().length > 0;
  const showSubtitle = !isMinimal && typeof subtitle === 'string' && subtitle.trim().length > 0;
  const showHeaderStrip = showKicker || showTitle || showSubtitle || hasSearch;

  const palette =
    tone === 'reading'
      ? {
          bg: 'var(--color-surface-subtle, #f9fafb)',
          text: 'var(--color-text-primary, #18181b)',
          muted: 'var(--color-text-tertiary, #71717a)',
          border: 'var(--color-border-default, rgba(0,0,0,0.08))',
          focus: 'var(--color-brand-600, #287f96)',
        }
      : tone === 'cinematic'
        ? {
            bg: 'var(--color-neutral-950, #0a0a0a)',
            text: 'var(--color-text-inverse, #fafafa)',
            muted: 'rgba(250,250,250,0.65)',
            border: 'var(--color-border-on-sunken, rgba(255,255,255,0.12))',
            focus: 'var(--color-brand-300, #74c9d8)',
          }
        : {
            bg: 'var(--color-surface-page, #fafafa)',
            text: 'var(--color-text-primary, #111111)',
            muted: 'var(--color-text-secondary, #71717a)',
            border: 'var(--color-border, rgba(0,0,0,0.08))',
            focus: 'var(--color-brand-600, #4f46e5)',
          };

  const cls = `cms-${mode}`;
  const css = `
    .${cls}-root {
      display: flex;
      flex-direction: column;
      ${isImmersive ? 'min-height: 100vh;' : ''}
      background: ${palette.bg};
      color: ${palette.text};
      padding-bottom: ${verticalPad}px;
    }
    .${cls}-skip {
      position: absolute;
      left: 8px;
      top: 8px;
      transform: translateY(-200%);
      background: ${palette.bg};
      color: ${palette.text};
      border: 1px solid ${palette.border};
      padding: 8px 12px;
      border-radius: 6px;
      font-size: 13px;
      z-index: 100;
      transition: transform 120ms ease;
    }
    .${cls}-skip:focus {
      transform: translateY(0);
      outline: 2px solid ${palette.focus};
      outline-offset: 2px;
    }
    .${cls}-strip {
      padding-top: ${stripPadY}px;
      padding-bottom: ${stripPadY}px;
      padding-left: max(24px, env(safe-area-inset-left));
      padding-right: max(24px, env(safe-area-inset-right));
      ${isMinimal ? '' : `border-bottom: 1px solid ${palette.border};`}
    }
    .${cls}-strip-inner {
      max-width: 1280px;
      margin: 0 auto;
      display: flex;
      align-items: ${isMinimal ? 'center' : 'flex-end'};
      justify-content: space-between;
      gap: 24px;
      flex-wrap: wrap;
    }
    .${cls}-search-wrap {
      flex: 0 0 auto;
      min-width: 240px;
    }
    .${cls}-body {
      flex: 1 1 auto;
      padding-top: ${isMinimal ? 16 : 32}px;
      padding-left: max(24px, env(safe-area-inset-left));
      padding-right: max(24px, env(safe-area-inset-right));
    }
    .${cls}-body-inner {
      max-width: 1280px;
      margin: 0 auto;
      display: grid;
      grid-template-columns: 1fr;
      gap: ${isMinimal ? 16 : 24}px;
      align-items: start;
    }
    @media (min-width: 1024px) {
      .${cls}-body-inner {
        grid-template-columns: ${gridForLg(hasSidebar, hasAside, isMinimal)};
        gap: ${isMinimal ? 24 : 40}px;
      }
      .${cls}-sidebar,
      .${cls}-aside {
        position: sticky;
        top: 24px;
        max-height: calc(100vh - 80px);
        overflow-y: auto;
      }
    }
    @media (min-width: 768px) and (max-width: 1023px) {
      .${cls}-body-inner {
        grid-template-columns: ${hasSidebar ? '220px minmax(0, 1fr)' : 'minmax(0, 1fr)'};
        gap: 24px;
      }
      .${cls}-sidebar {
        position: sticky;
        top: 24px;
        max-height: calc(100vh - 80px);
        overflow-y: auto;
      }
      .${cls}-aside { display: none; }
    }
    @media (max-width: 767px) {
      .${cls}-aside { display: none; }
      .${cls}-sidebar {
        order: -1;
        max-height: 220px;
        overflow-y: auto;
        border-bottom: 1px solid ${palette.border};
        padding-bottom: 16px;
      }
      .${cls}-sidebar a,
      .${cls}-sidebar button {
        min-height: 44px;
        display: flex;
        align-items: center;
      }
    }
    .${cls}-title {
      font-size: ${titleSize}px;
      font-weight: ${titleWeight};
      line-height: 1.15;
      letter-spacing: -0.01em;
      margin: 0;
      color: inherit;
      text-decoration: none;
      ${isImmersive ? 'font-family: var(--font-family-display, "Inter Tight", system-ui, sans-serif);' : ''}
    }
    @media (max-width: 600px) {
      .${cls}-title {
        font-size: ${Math.max(20, titleSize - 8)}px;
      }
    }
    .${cls}-title-link {
      color: inherit;
      text-decoration: none;
    }
    .${cls}-title-link:hover {
      text-decoration: underline;
      text-underline-offset: 4px;
    }
    .${cls}-title-link:focus-visible {
      outline: 2px solid ${palette.focus};
      outline-offset: 4px;
      border-radius: 4px;
    }
    .${cls}-kicker {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: ${palette.muted};
      margin-bottom: 8px;
      font-family: var(--font-family-mono, ui-monospace, "SF Mono", monospace);
    }
    .${cls}-subtitle {
      margin-top: 6px;
      font-size: ${isImmersive ? 16 : 14}px;
      color: ${palette.muted};
      max-width: 720px;
      line-height: 1.5;
    }
  `;

  return (
    <div className={`content-module-shell content-module-shell--${mode} ${cls}-root`}>
      <style dangerouslySetInnerHTML={{ __html: css }} />

      <PortalLink href={`#${cls}-main`} className={`${cls}-skip`}>
        {sbText('contentModule.skipToContent')}
      </PortalLink>

      {showHeaderStrip && (
        <header className={`${cls}-strip`}>
          <div className={`${cls}-strip-inner`}>
            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
              {showKicker && (
                <div className={`${cls}-kicker`} aria-hidden="true">
                  {kicker}
                </div>
              )}
              {showTitle && <TitleEl title={title} titleHref={titleHref} cls={cls} />}
              {showSubtitle && <p className={`${cls}-subtitle`}>{subtitle}</p>}
            </div>
            {hasSearch && (
              <search role="search" className={`${cls}-search-wrap`}>
                {search}
              </search>
            )}
          </div>
        </header>
      )}

      <div className={`${cls}-body`}>
        <div className={`${cls}-body-inner`}>
          {hasSidebar && (
            <nav className={`${cls}-sidebar`} aria-label={navLabel} style={{ minWidth: 0 }}>
              {sidebar}
            </nav>
          )}
          <main id={`${cls}-main`} style={{ minWidth: 0 }} tabIndex={-1}>
            {children}
          </main>
          {hasAside && !isMinimal && (
            <aside
              className={`${cls}-aside`}
              aria-label={tocLabel}
              style={{ minWidth: 0, fontSize: '13px', color: palette.muted }}
            >
              {aside}
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}

function gridForLg(hasSidebar: boolean, hasAside: boolean, isMinimal: boolean): string {
  const cols: string[] = [];
  if (hasSidebar) cols.push('260px');
  cols.push('minmax(0, 1fr)');
  if (hasAside && !isMinimal) cols.push('220px');
  return cols.join(' ');
}

function TitleEl({ title, titleHref, cls }: { title: string; titleHref?: string; cls: string }) {
  if (titleHref) {
    return (
      <h1 className={`${cls}-title`}>
        <Link href={titleHref} className={`${cls}-title-link`}>
          {title}
        </Link>
      </h1>
    );
  }
  return <h1 className={`${cls}-title`}>{title}</h1>;
}
